import React, { useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { mediaOutletUrl } from "views_shared/app/javascript/utils/urls";
import ProfilePage from "../profiles/ProfilePage";
import ConnectSection, { connectTitle } from "../profiles/sections/ConnectSection";
import { MediaOutletContext } from "./data/media_outlet_context_management";
import MediaOutletEditModal from "./edit/edit_modal";
import MediaOutletHeader from "./show/media_outlet_header";
import MediaOutletSections from "./show/media_outlet_sections";
import MediaOutletSkeleton from "./show/media_outlet_skeleton";

// Custom hook to get the previous value
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// Media Outlet show (the inner component under MediaOutletTopLevelShow's provider): the shared
// <ProfilePage> chassis with the no-cover <EntityHeader> + section cards, plus a right rail carrying
// the Contact card. Unlike the similarity/team-members rail cards on other engines, Contact needs no
// loading-aware railHasContent guard: contactInfos and editable settle on the same provider promise
// as mediaOutlet itself, so there's no reflow case where the rail would need to appear after the page
// has already rendered.
function MediaOutlet({ mediaOutletId }) {
  const { setMediaOutletId, isLoading, asyncError, mediaOutlet, contactInfos, viewerContext, setOpenEditModal } =
    useContext(MediaOutletContext);
  const editable = !!viewerContext?.editable;

  const currentSlug = mediaOutlet?.slug;
  const previousSlug = usePrevious(currentSlug);

  useEffect(() => {
    setMediaOutletId(mediaOutletId);
  }, [mediaOutletId, setMediaOutletId]);

  // Renaming an outlet resets its slug server-side, so the URL in the address bar stops resolving to
  // this outlet the moment the post-save reload lands. Follow it to the new one.
  // V1-COMPAT: on V1 the equivalent page is the id-based /publications/:id (see mediaOutletUrl in
  // views_shared/utils/urls), which a slug change never affects, so there is nothing to redirect.
  useEffect(() => {
    if (globalThis.Qwoted?.V1_HOSTED) return;
    if (previousSlug && currentSlug && previousSlug !== currentSlug) {
      window.location.href = mediaOutletUrl({ slug: currentSlug, migrate_id: mediaOutlet?.migrate_id });
    }
  }, [previousSlug, currentSlug, mediaOutlet?.migrate_id]);

  // Let the top-level ErrorBoundary handle async failures.
  if (asyncError) throw asyncError;

  // Rails found the outlet but the API returned no row — the viewer isn't entitled to it (the
  // PostgREST view enforces visibility/RLS; the Rails route doesn't). Say so instead of
  // skeleton-ing forever.
  if (!isLoading && !mediaOutlet) {
    return <div className="alert alert-secondary">This media outlet isn&apos;t available.</div>;
  }

  return (
    <div>
      <MediaOutletEditModal />

      <ProfilePage
        isLoading={isLoading}
        skeleton={<MediaOutletSkeleton />}
        header={<MediaOutletHeader />}
        rightRail={
          editable || contactInfos.length > 0 ? (
            <ConnectSection
              id="media-outlet-section-contact"
              title={connectTitle(mediaOutlet?.display_name || mediaOutlet?.name)}
              contactDetails={contactInfos}
              editable={editable}
              onEdit={() => setOpenEditModal("CONTACT")}
              editButtonId="modal-button-CONTACT"
            />
          ) : null
        }
      >
        <MediaOutletSections />
      </ProfilePage>
    </div>
  );
}

MediaOutlet.propTypes = {
  mediaOutletId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default MediaOutlet;
